import { Component, OnInit } from '@angular/core';
import { NgIf, NgFor , NgClass } from '@angular/common';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { FormsModule } from '@angular/forms';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import Swal from 'sweetalert2';
import { InvoiceService } from '../../../../Services/Invoice/invoice.service';

@Component({
  selector: 'app-invoice-report',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule , NgClass],
  templateUrl: './invoice-report.component.html',
  styleUrl: './invoice-report.component.css'
})
export class InvoiceReportComponent implements OnInit {

  invoiceList: any[] = [];
  paginatedInvoiceList: any[] = [];

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;

  globalSearchText: string = '';
  searchValues: { [key: string]: string } = {};

  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  showDropdown: boolean = false;

  previewImage: string | null = null;

  selectedFields = [
    { key: 'invoice_number', label: 'INV No.', selected: true },
    { key: 'invoice_date', label: 'DATE', selected: true },
    { key: 'firm_name', label: 'FIRM', selected: true },
    { key: 'customer_name', label: 'CUST NAME', selected: true },
    { key: 'customer_mob', label: 'MOBILE', selected: true },
    { key: 'previous_amount', label: 'PREV AMT', selected: true },
    { key: 'amount_paid', label: 'AMT PAID', selected: true },
    { key: 'total_amount_before_disc', label: 'AMT', selected: true },
    { key: 'cgst', label: 'CGST', selected: true },
    { key: 'sgst', label: 'SGST', selected: true },
    { key: 'grand_total', label: 'TOT AMT', selected: true },
    { key: 'metal_received', label: 'MET AMT', selected: true },
    { key: 'discount_paid', label: 'DISCOUNT', selected: true },
    { key: 'cash_amount', label: 'AMT BY CASH', selected: true },
   // { key: 'bank_amount', label: 'AMT BY CHEQUE', selected: true },
   // { key: 'card_amount', label: 'AMT BY CARD', selected: true }
  ];
  

  constructor(private invoiceService: InvoiceService) {}

  ngOnInit(): void {
    const savedSelections = localStorage.getItem('invoiceColumnSelections');
    if (savedSelections) {
      this.selectedFields = JSON.parse(savedSelections);
    }
    this.getInvoices();
  }

  getInvoices() {
    this.invoiceService.getInvoices().subscribe((res: any) => {
      this.invoiceList = res.data ?? res;
      this.totalPages = Math.ceil(this.invoiceList.length / this.itemsPerPage);
      this.currentPage = 1;
      this.filterTable();
    });
  }
  

  filterTable() {
    let filtered = this.invoiceList.filter(item => {
      const matchesFieldFilters = this.selectedFields.every(field => {
        const searchValue = this.searchValues[field.key]?.trim().toLowerCase() || '';
        if (!searchValue) return true;

        return String(item[field.key] ?? '')
          .toLowerCase()
          .includes(searchValue);
      });

      const matchesGlobalSearch = this.globalSearchText
        ? Object.values(item).some(val =>
          String(val ?? '').toLowerCase().includes(this.globalSearchText.toLowerCase())
        )
        : true;

      return matchesFieldFilters && matchesGlobalSearch;
    });

    if (this.sortKey) {
      filtered.sort((a, b) =>
        this.sortDirection === 'asc'
          ? String(a[this.sortKey]).localeCompare(String(b[this.sortKey]))
          : String(b[this.sortKey]).localeCompare(String(a[this.sortKey]))
      );
    }

    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage) || 1;

    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedInvoiceList = filtered.slice(start, start + this.itemsPerPage);
  }

  sortBy(key: string) {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDirection = 'asc';
    }
    this.filterTable();
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  applyColumnSelection() {
    this.saveSelectedColumns();
    this.filterTable();
    this.showDropdown = false;
  }

  saveSelectedColumns() {
    localStorage.setItem('invoiceColumnSelections', JSON.stringify(this.selectedFields));
  }

  selectAllFields() {
    this.selectedFields.forEach(f => f.selected = true);
    this.applyColumnSelection();
  }

  deselectAllFields() {
    this.selectedFields.forEach(f => f.selected = false);
    this.applyColumnSelection();
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.filterTable();
  }

  goToPreviousPage() {
    if (this.currentPage > 1) this.currentPage--;
    this.filterTable();
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) this.currentPage++;
    this.filterTable();
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages; i++) pages.push(i);
    return pages;
  }

  previewInvoice(invoiceId: number) {
    const popup = window.open(`/rough-estimate-invoice/${invoiceId}`, "_blank", "width=1080,height=800");
    if (!popup) return;

    popup.onload = async () => {
      const element = popup.document.getElementById('invoice-capture');
      if (!element) return;

      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      this.previewImage = canvas.toDataURL('image/png');

      popup.close();
    }
  }

  closePreview() {
    this.previewImage = null;
  }

  downloadCSV(): void {
    const selectedFields = this.selectedFields.filter(f => f.selected);
    const header = ['No.', ...selectedFields.map(f => f.label)];

    let csv = header.join(',') + '\n';

    this.invoiceList.forEach((item, index) => {
      const row = [
        (this.currentPage - 1) * this.itemsPerPage + index + 1,
        ...selectedFields.map(f => item[f.key] ?? '')
      ];
      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, 'invoice-report.csv');
  }

 printTable() {

  console.log('DEBUG invoice row:', this.invoiceList[0]);

  let printContent = `
  <html>
  <head>
    <title>Item Sold Invoice</title>

    <style>

body {
  font-family: Arial, sans-serif;
  margin: 0;
}

h3 {
  text-align: center;
  margin-bottom: 12px;
}

table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12px;
      }

      th,
      td {
        border-right: 2px solid #000;
        border-bottom: 2px solid #000;
        padding: 8px;
        text-align: left;
        vertical-align: middle;
        white-space: nowrap;
      }

      th:first-child,
      td:first-child {
        border-left: 2px solid #000;
      }

      thead th {
        border-top: 2px solid #000;
        background-color: #f2f2f2;
        font-weight: 600;
      }

      tbody tr:last-child td {
        border-bottom: 2px solid #000;
      }

@media print {
  @page {
   size: auto;
    margin: 8mm;
  }
}

    </style>

  </head>

  <body>

    <h3>ITEM SOLD INVOICE</h3>

    <table>
      <thead>
        <tr>
          <th>No.</th>
  `;

  // ===== HEADERS =====
  this.selectedFields.forEach(field => {
    if (field.selected) {
      printContent += `<th>${field.label}</th>`;
    }
  });

  printContent += `
        </tr>
      </thead>
      <tbody>
  `;

  // ===== ROWS =====
  this.invoiceList.forEach((item, index) => {

    printContent += `
      <tr>
        <td>${(this.currentPage - 1) * this.itemsPerPage + index + 1}</td>
    `;

    this.selectedFields.forEach(field => {

      if (!field.selected) return;

      const value = item[field.key];

      let displayValue = '';

      /* ======================
         OBJECT HANDLING
      ====================== */

      if (value && typeof value === 'object') {

        displayValue =
          value.name ||
          value.title ||
          value.invoice_number ||
          '';

      }

      /* ======================
         NORMAL VALUE
      ====================== */

      else {

        displayValue = value ?? '-';

      }

      printContent += `<td>${displayValue}</td>`;

    });

    printContent += `</tr>`;

  });

  printContent += `
      </tbody>
    </table>

  </body>
  </html>
  `;

  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  }
}

  downloadPDF() {
    const doc = new jsPDF('l', 'mm', 'a4');
    const selectedFields = this.selectedFields.filter(f => f.selected);

    const headers = [
      { header: 'No.', dataKey: 'no' },
      ...selectedFields.map(f => ({ header: f.label, dataKey: f.key }))
    ];

    const data = this.invoiceList.map((item, i) => {
      const row: any = {
        no: (this.currentPage - 1) * this.itemsPerPage + i + 1
      };
      selectedFields.forEach(f => row[f.key] = item[f.key] ?? '');
      return row;
    });

    autoTable(doc, {
      columns: headers,
      body: data,
      startY: 10,
      theme: 'grid'
    });

    doc.save('invoice-report.pdf');
  }

 downloadExcel(): void {
  const selectedFields = this.selectedFields.filter(f => f.selected);

  // ✅ MUST match backend keys exactly
  const numericKeys = [
    'previous_amount',
    'amount_paid',
    'total_amount_before_disc',
    'cgst',
    'sgst',
    'grand_total',
    'metal_received',
    'discount_paid',
    'cash_amount'
  ];

  const data = this.invoiceList.map((item, i) => {
    const row: any = {
      'No.': (this.currentPage - 1) * this.itemsPerPage + i + 1
    };

    selectedFields.forEach(f => {
      let value = item[f.key];

      // ✅ FORCE numbers
      if (numericKeys.includes(f.key)) {
        value =
          value !== null &&
          value !== undefined &&
          value !== ''
            ? parseFloat(value)
            : 0;
      }

      row[f.label] = value;
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(data, {
    cellDates: false
  });

  // ✅ HARD force numeric type in sheet
  Object.keys(worksheet).forEach(cell => {
    if (cell.startsWith('!')) return;

    const c = worksheet[cell];

    if (!isNaN(c.v) && typeof c.v !== 'string') {
      c.t = 'n';
    }
  });

  const workbook = {
    Sheets: { 'Invoice Report': worksheet },
    SheetNames: ['Invoice Report']
  };

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  FileSaver.saveAs(
    new Blob([excelBuffer], {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }),
    'invoice-report.xlsx'
  );
}

  downloadJSONAll() {
    const blob = new Blob([JSON.stringify(this.invoiceList, null, 2)],
      { type: 'application/json' });
    FileSaver.saveAs(blob, 'invoice-report.json');
  }

  deleteInvoice(id: number, invoiceNumber?: string) {
    const label = invoiceNumber ? `invoice ${invoiceNumber}` : 'this invoice';

    Swal.fire({
      title: `Delete ${label}?`,
      text: 'Put sold items back into stock?',
      icon: 'warning',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes',
      denyButtonText: 'No',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#198754',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    }).then((result) => {
      if (result.isDismissed) {
        return;
      }

      const restoreStock = result.isConfirmed;
      this.invoiceService.deleteInvoice(id, restoreStock).subscribe({
        next: () => {
          this.invoiceList = this.invoiceList.filter((inv) => inv.id !== id);
          this.filterTable();
        },
        error: (err) => {
          const message = err?.error?.message || 'Failed to delete invoice. Please try again.';
          alert(message);
        }
      });
    });
  }
}
